import React, { useEffect, useRef, useState } from 'react';
import { useResendEmailVerificationCode } from '../../../hooks/apolloHooks/useResendEmailVerificationCode';
import { InfoIconOutlined } from '../../svgs/ExpressonSVGs';
import Tooltip from '../Tooltip';

type SetMainEmailProps = {
    // Email input
    emailValue: string
    emailError: string | undefined
    isEmailValid: boolean
    loading: boolean
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleEmailVerify: () => void
    // Verification code input (shown after email is submitted)
    emailVerificationSent: boolean
    addedEmailId: number | null
    codeValue: string
    setCodeValue: (value: string) => void
    verifyLoading: boolean
    handleCodeVerification: () => void
    onEditEmail: () => void
    isEmailInputDisabled: boolean
    // Error state
    submitError: string | null
}

const SetMainEmail: React.FC<SetMainEmailProps> = ({
    emailValue,
    emailError,
    isEmailValid,
    loading,
    handleEmailChange,
    handleEmailVerify,
    emailVerificationSent,
    addedEmailId,
    codeValue,
    setCodeValue,
    verifyLoading,
    handleCodeVerification,
    onEditEmail,
    isEmailInputDisabled,
    submitError,
}) => {
    const [cooldownSeconds, setCooldownSeconds] = useState(0)
    const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const emailInputRef = useRef<HTMLInputElement>(null)
    const codeInputRef = useRef<HTMLInputElement>(null)
    const { resendCode } = useResendEmailVerificationCode()

    const handleResend = async () => {
        if (!addedEmailId || cooldownSeconds > 0) return
        try {
            await resendCode({ emailId: addedEmailId })
        } catch (err) {
            console.error('[SetMainEmail] resendCode failed:', err)
        }
        // Start 5-second cooldown regardless of success/failure
        setCooldownSeconds(30)
        cooldownRef.current = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current!)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    // Cleanup interval on unmount
    useEffect(() => {
        return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
    }, [])

    // Auto-focus code input when it appears
    useEffect(() => {
        if (emailVerificationSent) {
            setTimeout(() => codeInputRef.current?.focus(), 0)
        }
    }, [emailVerificationSent])

    return (
        <div className="add-email">
            <div className="input-wrapper">
                <label htmlFor="email">E-postadress</label>
                <input
                    ref={emailInputRef}
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Ange din e-postadress"
                    value={emailValue}
                    disabled={isEmailInputDisabled}
                    onChange={handleEmailChange}
                />
                <span>Vi skickar en verifieringskod till denna adress.</span>
            </div>

            {emailVerificationSent && (
                <div className="input-wrapper verification">
                    <label htmlFor="verificationCode">
                        Verifieringskod
                        <Tooltip position="top-end" text="Om du inte har fått någon kod, kontrollera din e-post du skrivit in, skräppostmapp eller klicka på 'Skicka verifieringskod igen'." >
                            <InfoIconOutlined width={25} height={26} />
                        </Tooltip>
                    </label>
                    <input
                        ref={codeInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="verificationCode"
                        name="verificationCode"
                        placeholder="Ange verifieringskoden"
                        maxLength={4}
                        value={codeValue}
                        onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, ''))}
                    />
                    <span>Fyll i koden du fick i mejlet för att verifiera din e-postadress.</span>
                    <span
                        className={`resend-verification${cooldownSeconds > 0 ? ' resend-verification--cooldown' : ''}`}
                        onClick={handleResend}
                    >
                        {cooldownSeconds > 0
                            ? `Skicka igen om ${cooldownSeconds}s`
                            : 'Skicka verifieringskod igen'
                        }
                    </span>
                </div>
            )}

            {emailError && emailValue.length > 0 && (
                <span className="input-error">{emailError}</span>
            )}
            {submitError && (
                <span className="input-error">{submitError}</span>
            )}

            <div className="button-wrapper">
                {!emailVerificationSent ? (
                    <>
                        <button
                            type="button"
                            className="button-edit body-text"
                            disabled={!isEmailValid || loading}
                            onClick={handleEmailVerify}
                        >
                            {loading ? 'Skickar...' : 'Skicka verifieringskod'}
                        </button>
                        {isEmailInputDisabled && (
                            <button
                                type="button"
                                className="button-edit body-text"
                                onClick={() => {
                                    onEditEmail()
                                    setTimeout(() => emailInputRef.current?.focus(), 0)
                                }}
                            >
                                Redigera e-postadress
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className="button-edit body-text"
                            disabled={codeValue.length !== 4 || verifyLoading}
                            onClick={handleCodeVerification}
                        >
                            {verifyLoading ? 'Verifierar...' : 'Verifiera E-post'}
                        </button>
                        <button
                            type="button"
                            className="button-edit body-text"
                            disabled={codeValue.length === 4}
                            onClick={() => {
                                onEditEmail()
                                setTimeout(() => emailInputRef.current?.focus(), 0)
                            }}
                        >
                            Redigera e-postadress
                        </button>
                    </>
                )}
            </div>

        </div>
    )
}

export default SetMainEmail;
